// src/pages/healthcare/appointments/components/BlockOutTimeModal.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, DatePicker, Input, Modal, Popconfirm, Select, Space, Switch, TimePicker, Typography } from "antd";
import type { Dayjs } from "dayjs";

import { dayjs } from "../../../../utils/time";
import { formatLocalTime } from "../../../../utils/userPrefs/DateTimeUtils";
import { BLOCK_TYPES, BlockRef, BlockType, normaliseBlockType } from "../../../../utils/appointments/availability";
import { ConflictingAppointment, conflictsForPractitioners, fetchConflictingAppointments, newlyConflicting } from "../../../../api/healthcare/Appointment/AppointmentApi";
import { useCreateUnavailableBlocks, useDeleteUnavailableBlock, useUpdateUnavailableBlock } from "../../../../hooks/appointment/usePractitionerAvailability";
import { TeamMember } from "../../../../types/admin/TeamTypes";
import { SlotSelection } from "./SlotActionPopover";
import type { UserUuid } from "../../../../types/identity";

const TYPE_LABELS: Record<BlockType, string> = {
	lunch: "Lunch",
	admin: "Admin",
	meeting: "Meeting",
	training: "Training",
	leave: "Leave",
	other: "Other",
};

export const lastBlockTypeKey = (userId?: UserUuid | null): string => `mg.appointments.lastBlockType.v1.${userId ?? "anon"}`;

const readLastBlockType = (userId?: UserUuid | null): BlockType => {
	try {
		return normaliseBlockType(window.localStorage.getItem(lastBlockTypeKey(userId)));
	} catch {
		return "other";
	}
};

const rememberBlockType = (userId: UserUuid | null | undefined, blockType: BlockType): void => {
	try {
		window.localStorage.setItem(lastBlockTypeKey(userId), blockType);
	} catch {
		// A blocked store just means the next dialog opens on the default.
	}
};

interface BlockOutTimeModalProps {
	// One of these two: `selection` is the dragged range that creates a block, `block` the stored row being edited.
	selection?: SlotSelection;
	block?: BlockRef;
	teamMembers: TeamMember[];
	canManageSchedule: boolean;
	userId?: UserUuid | null;
	// Week (multi) mode only — the caller's ticked set and the legend's visible set. The Day and edit paths ignore both.
	defaultPractitionerIds?: UserUuid[];
	visibleIds?: Set<UserUuid>;
	onClose: () => void;
}

const BlockOutTimeModal: React.FC<BlockOutTimeModalProps> = ({
	selection,
	block,
	teamMembers,
	canManageSchedule,
	userId,
	defaultPractitionerIds = [],
	visibleIds = new Set<UserUuid>(),
	onClose,
}) => {
	const createBlocks = useCreateUnavailableBlocks();
	const updateBlock = useUpdateUnavailableBlock();
	const deleteBlock = useDeleteUnavailableBlock();

	// D7: a drag with no practitioner column came from the all-staff week, and only that drag picks a set.
	const multi = !block && selection?.practitionerId === null;

	const describe = (target: UserUuid | null): string => {
		if (target === null) return "the clinic";
		const member = teamMembers.find((teamMember) => teamMember.id === target);
		// Never a raw id in a user-facing toast.
		return member ? `${member.first_name} ${member.last_name}`.trim() : "this practitioner";
	};

	const rosterIds = teamMembers.map((member) => member.id);
	// Roster order and roster members only, so the tags read like the legend and an empty set is the only way Save is off.
	const fromRoster = (ids: Iterable<UserUuid>): UserUuid[] => {
		const wanted = new Set(ids);
		return rosterIds.filter((id) => wanted.has(id));
	};
	const selfOnRoster = userId != null && rosterIds.includes(userId);

	const seed = block ?? selection;

	const [practitionerId, setPractitionerId] = useState<UserUuid>((block ? block.userId : selection?.practitionerId) ?? userId ?? "");
	// D2: a manager opens on the caller's ticked set; anyone else is locked to themselves.
	const [practitionerIds, setPractitionerIds] = useState<UserUuid[]>(() => (multi ? fromRoster(canManageSchedule ? defaultPractitionerIds : userId != null ? [userId] : []) : []));
	const [date, setDate] = useState<Dayjs>(dayjs(seed?.start));
	const [startTime, setStartTime] = useState<Dayjs>(dayjs(seed?.start));
	const [endTime, setEndTime] = useState<Dayjs>(dayjs(seed?.end));
	const [blockType, setBlockType] = useState<BlockType>(() => (block ? block.blockType : readLastBlockType(userId)));
	const [reason, setReason] = useState<string>(block?.reason ?? "");
	const [clinicWide, setClinicWide] = useState<boolean>(block ? block.source === "closure" : false);
	const [conflicts, setConflicts] = useState<ConflictingAppointment[] | null>(null);
	const [checkFailed, setCheckFailed] = useState<boolean>(false);
	const [checking, setChecking] = useState<boolean>(false);

	// The date pickers hold a wall clock the practitioner reads; the instants below are what the block is.
	const range = useMemo(() => {
		const at = (time: Dayjs) => date.hour(time.hour()).minute(time.minute()).second(0).millisecond(0);
		return { start: at(startTime), end: at(endTime) };
	}, [date, startTime, endTime]);

	// A changed range or target invalidates the conflict answer, so the next save re-checks it.
	useEffect(() => {
		setConflicts(null);
		setCheckFailed(false);
	}, [practitionerId, practitionerIds, clinicWide, date, startTime, endTime]);

	const rangeInvalid = !range.end.isAfter(range.start);
	const warned = conflicts !== null || checkFailed;
	const targets: Array<UserUuid | null> = clinicWide ? [null] : multi ? practitionerIds : practitionerId ? [practitionerId] : [];
	// A closure is one bucket and keeps the flat list; only a practitioner set is worth splitting by name.
	const grouped = multi && !clinicWide;
	// PUT drops user_id server-side, so an edit asks about the block's own route; a set asks about the whole clinic once and splits the answer.
	const conflictScope = block ? block.userId : clinicWide || multi ? null : practitionerId;
	const canDelete = block ? (block.userId === null ? canManageSchedule : canManageSchedule || block.userId === userId) : false;

	// Responsive tags hide overflow behind "+N", so the button is the one place the true scope is never hidden.
	const scope = clinicWide ? "Close the clinic" : targets.length === 1 ? `Block out ${describe(targets[0])}` : `Block out ${targets.length} practitioners`;
	const saveLabel = multi ? (warned ? `${scope} anyway` : scope) : warned ? (block ? "Save anyway" : "Create block anyway") : "Save";

	const handleSave = async () => {
		if (rangeInvalid || (!block && targets.length === 0)) return;

		if (!warned) {
			setChecking(true);
			try {
				const found = newlyConflicting(await fetchConflictingAppointments(conflictScope, range.start.toDate(), range.end.toDate()), block);
				// The whole-clinic answer includes practitioners outside the set; only the chosen ones' appointments count.
				const relevant = grouped ? [...new Set([...conflictsForPractitioners(found, targets).values()].flat())] : found;
				setConflicts(relevant);
				if (relevant.length > 0) {
					setChecking(false);
					return;
				}
			} catch {
				setCheckFailed(true);
				setChecking(false);
				return;
			}
			setChecking(false);
		}

		const payload = {
			// ⚠️ UTC INSTANTS. The override path's schedule_data is HH:mm wall clock; the two are never interchangeable.
			starts_at: range.start.toISOString(),
			ends_at: range.end.toISOString(),
			block_type: blockType,
			reason: reason.trim() || null,
		};

		if (block) {
			updateBlock.mutate({ userId: block.userId, blockId: block.id, payload });
		} else {
			rememberBlockType(userId, blockType);
			createBlocks.mutate({ targets, payload, describe });
		}
		onClose();
	};

	const handleDelete = () => {
		if (!block) return;
		deleteBlock.mutate({ userId: block.userId, blockId: block.id });
		onClose();
	};

	const practitionerOptions = teamMembers.map((member) => ({ value: member.id, label: `${member.first_name} ${member.last_name}`.trim() }));

	const conflictList = (rows: ConflictingAppointment[]) => (
		<ul style={{ margin: "4px 0 8px", paddingLeft: 18 }}>
			{rows.map((conflict) => (
				<li key={conflict.id}>
					{formatLocalTime(conflict.start)} – {formatLocalTime(conflict.end)} · {conflict.patient}
				</li>
			))}
		</ul>
	);

	return (
		<Modal
			open
			title={block ? "Edit blocked time" : "Block out time"}
			onCancel={onClose}
			destroyOnClose
			width={520}
			footer={[
				canDelete ? (
					<Popconfirm
						key="delete"
						title="Delete this block?"
						description="The time becomes bookable again. Any appointments in it are unaffected."
						okText="Delete"
						okButtonProps={{ danger: true, "data-testid": "appointments-block-delete-confirm" } as never}
						onConfirm={handleDelete}
					>
						<Button danger type="text" style={{ float: "left" }} data-testid="appointments-block-delete">
							Delete
						</Button>
					</Popconfirm>
				) : null,
				<Button key="cancel" onClick={onClose}>
					Cancel
				</Button>,
				// `disabled` rather than `loading`: antd swallows the click that lands on a loading -> idle transition.
				<Button key="save" type="primary" disabled={rangeInvalid || checking || (!block && targets.length === 0)} onClick={handleSave} data-testid="appointments-block-save">
					{saveLabel}
				</Button>,
			]}
		>
			<Space direction="vertical" size={12} style={{ display: "flex" }} data-testid="appointments-block-modal">
				<div>
					<Typography.Text type="secondary">{multi ? "Practitioners" : "Practitioner"}</Typography.Text>
					{multi ? (
						<Select
							mode="multiple"
							value={practitionerIds}
							onChange={setPractitionerIds}
							disabled={!canManageSchedule || clinicWide}
							showSearch
							optionFilterProp="label"
							maxTagCount="responsive"
							placeholder="Choose practitioners"
							style={{ width: "100%" }}
							data-testid="appointments-block-practitioner"
							options={practitionerOptions}
						/>
					) : (
						<Select
							value={practitionerId}
							onChange={setPractitionerId}
							disabled={Boolean(block) || !canManageSchedule || clinicWide}
							style={{ width: "100%" }}
							data-testid="appointments-block-practitioner"
							options={practitionerOptions}
						/>
					)}
					{multi && canManageSchedule && (
						<Space size={4}>
							<Button size="small" type="link" disabled={clinicWide} onClick={() => setPractitionerIds(fromRoster(visibleIds))} data-testid="appointments-block-pick-shown">
								All shown
							</Button>
							{selfOnRoster && (
								<Button size="small" type="link" disabled={clinicWide} onClick={() => setPractitionerIds(userId != null ? [userId] : [])} data-testid="appointments-block-pick-me">
									Only me
								</Button>
							)}
							<Button size="small" type="link" disabled={clinicWide} onClick={() => setPractitionerIds([])} data-testid="appointments-block-pick-clear">
								Clear
							</Button>
						</Space>
					)}
				</div>

				<Space size={12} wrap>
					<div>
						<Typography.Text type="secondary">Date</Typography.Text>
						<DatePicker value={date} onChange={(value) => value && setDate(value)} allowClear={false} style={{ display: "block" }} data-testid="appointments-block-date" />
					</div>
					<div>
						<Typography.Text type="secondary">From</Typography.Text>
						<TimePicker value={startTime} onChange={(value) => value && setStartTime(value)} format="HH:mm" minuteStep={5} allowClear={false} style={{ display: "block" }} data-testid="appointments-block-start" />
					</div>
					<div>
						<Typography.Text type="secondary">To</Typography.Text>
						<TimePicker value={endTime} onChange={(value) => value && setEndTime(value)} format="HH:mm" minuteStep={5} allowClear={false} style={{ display: "block" }} data-testid="appointments-block-end" />
					</div>
				</Space>

				{rangeInvalid && <Alert type="error" showIcon message="The end time must be after the start time." data-testid="appointments-block-range-error" />}

				{checking && <Typography.Text type="secondary">Checking for appointments in this time…</Typography.Text>}

				<div>
					<Typography.Text type="secondary">Type</Typography.Text>
					<Select
						value={blockType}
						onChange={setBlockType}
						style={{ width: "100%" }}
						data-testid="appointments-block-type"
						options={BLOCK_TYPES.map((type) => ({ value: type, label: TYPE_LABELS[type] }))}
					/>
				</div>

				<div>
					<Typography.Text type="secondary">Note (optional)</Typography.Text>
					<Input.TextArea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={255} rows={2} placeholder="Dentist appt 2pm" data-testid="appointments-block-reason" />
				</div>

				{canManageSchedule && (
					<Space size={8}>
						<Switch checked={clinicWide} onChange={setClinicWide} disabled={Boolean(block)} data-testid="appointments-block-clinic-wide" />
						<Typography.Text>Applies to the whole clinic</Typography.Text>
					</Space>
				)}

				{block && <Typography.Text type="secondary">Who this block belongs to cannot be changed — delete it and create a new one instead.</Typography.Text>}

				{conflicts !== null && conflicts.length > 0 && (
					<Alert
						type="warning"
						showIcon
						data-testid="appointments-block-conflicts"
						message={`${conflicts.length} appointment${conflicts.length === 1 ? "" : "s"} already booked in this time`}
						description={
							<>
								{grouped
									? [...conflictsForPractitioners(conflicts, targets)]
											.filter(([, rows]) => rows.length > 0)
											.map(([id, rows]) => (
												<div key={String(id)} data-testid="appointments-block-conflicts-group">
													<Typography.Text strong>{describe(id)}</Typography.Text>
													{conflictList(rows)}
												</div>
											))
									: conflictList(conflicts)}
								<Typography.Text strong>These appointments are not cancelled or moved. Save again to create the block anyway.</Typography.Text>
							</>
						}
					/>
				)}

				{checkFailed && (
					<Alert
						type="warning"
						showIcon
						data-testid="appointments-block-conflict-check-failed"
						message="Existing appointments could not be checked"
						description="Save again to create the block anyway — any appointments in this time are not cancelled or moved."
					/>
				)}
			</Space>
		</Modal>
	);
};

export default BlockOutTimeModal;
